import React, { useState, useEffect } from 'react';
import {
    Home,
    Users,
    MessageSquare,
    User,
    Bell,
    MoreHorizontal,
    Heart,
    ChevronLeft,
    ChevronRight,
    Wand2,
    Tag,
    X,
    Zap,
    Sparkles
} from 'lucide-react';
import ProfileModal from './ProfileModal.tsx';
import SuggestionsModal from './SuggestionsModal.tsx';
import { useTheme } from '../context/ThemeContext.tsx';
import { useIsMobile } from '../hooks/useIsMobile.ts';
import { API_URL } from '../config/api.ts';

interface SidebarProps {
    isOpen?: boolean;
    onClose?: () => void;
    onCreateCharacter?: () => void;
    onMyCharacters?: () => void;
    userData?: {
        username: string;
        avatar?: string | null;
        isPro?: boolean;
        usage?: {
            messages: number;
            messageLimit: number;
            messagesAvailable?: number;
            boosterMessages?: number;
        };
        gender?: string;
        birthday?: string;
        bio?: string;
        email?: string;
        isOAuthUser?: boolean;
        hasPassword?: boolean;
    };
    mode?: 'default' | 'chat';
    onHome?: () => void;
    onProfileUpdate?: () => void;
    onCollapse?: (collapsed: boolean) => void;
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
    onLogin?: () => void;
    onLogout?: () => void;
    onOpenPricing?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCreateCharacter, onMyCharacters, onHome, onProfileUpdate, onCollapse, userData: propUserData, mode = 'default', isMobileOpen = false, onMobileClose, onLogin, onLogout, onOpenPricing }) => {
    const { isDarkMode } = useTheme();
    const isMobile = useIsMobile();
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isSuggestionsModalOpen, setIsSuggestionsModalOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);

    // Use propUserData or fallback
    const userData = propUserData || {
        username: 'Guest',
        avatar: null
    };

    const handleUpdateProfile = async (data: any) => {
        try {
            const response = await fetch(`${API_URL}/user`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data),
                credentials: 'include'
            });
            const result = await response.json();
            if (result.success) {
                console.log('Profile updated successfully');
                if (onProfileUpdate) onProfileUpdate();
            } else {
                console.error('Failed to update profile:', result.message);
                throw new Error(result.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    };

    return (
        <>
            {/* Mobile backdrop */}
            {isMobile && isMobileOpen && (
                <div
                    onClick={onMobileClose}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9998,
                        backdropFilter: 'blur(4px)',
                        animation: 'fadeIn 0.3s ease'
                    }}
                />
            )}
            <style>{`
                @keyframes slideInLeft {
                    from { transform: translateX(-100%); }
                    to { transform: translateX(0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: isMobile ? '64px' : 0, // Leave space for bottom nav on mobile
                width: isMobile ? '280px' : (isCollapsed ? '70px' : '240px'),
                background: isDarkMode
                    ? 'rgb(26, 26, 46)'
                    : 'linear-gradient(180deg, #f3e8ff 0%, #fae8ff 100%)',
                borderRight: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(168, 85, 247, 0.2)'}`,
                display: 'flex',
                flexDirection: 'column',
                padding: isMobile ? '20px 16px' : (isCollapsed ? '20px 8px' : '20px 16px'),
                zIndex: 9999,
                transition: isMobile ? 'transform 0.3s ease' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isMobile ? (isMobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
                boxShadow: `4px 0 24px ${isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(168, 85, 247, 0.1)'}`,
                overflow: 'hidden',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch'
            }}>
                <style>{`
                    div::-webkit-scrollbar {
                        display: none;
                    }
                `}</style>

                {/* Mobile Close Button */}
                {isMobile && (
                    <button
                        onClick={onMobileClose}
                        style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: isDarkMode ? 'white' : '#333'
                        }}
                    >
                        <X size={18} />
                    </button>
                )}

                {/* Toggle Button - Desktop only */}
                {!isMobile && (
                    <button
                        onClick={() => {
                            const newState = !isCollapsed;
                            setIsCollapsed(newState);
                            if (onCollapse) onCollapse(newState);
                        }}
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: isCollapsed ? '8px' : '12px',
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            cursor: 'pointer',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                            transition: '0.2s'
                        }}
                    >
                        {isCollapsed ? <ChevronRight size={16} strokeWidth={3} /> : <ChevronLeft size={16} strokeWidth={3} />}
                    </button>
                )}
                {/* Logo */}

                {/* Logo */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '40px',
                    padding: isCollapsed ? '0' : '0 8px',
                    marginTop: '20px',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    whiteSpace: 'nowrap'
                }}>
                    <div style={{
                        width: isCollapsed ? '44px' : '40px',
                        height: isCollapsed ? '44px' : '40px',
                        background: 'linear-gradient(135deg, rgb(168, 85, 247) 0%, rgb(236, 72, 153) 100%)',
                        borderRadius: isCollapsed ? '50%' : '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        flexShrink: 0,
                        transition: 'all 0.3s ease'
                    }}>
                        <Heart fill="white" size={isCollapsed ? 24 : 20} />
                    </div>
                    {!isCollapsed && (
                        <span style={{
                            fontSize: '24px',
                            fontWeight: '800',
                            background: 'linear-gradient(135deg, rgb(168, 85, 247) 0%, rgb(236, 72, 153) 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            whiteSpace: 'nowrap'
                        }}>
                            EcstasyAI
                        </span>
                    )}
                </div>

                {/* Mobile Profile Section */}
                {isMobile && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '16px',
                        marginBottom: '20px',
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                        borderRadius: '16px',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`
                    }}>
                        {/* Avatar */}
                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '12px',
                            overflow: 'hidden'
                        }}>
                            {userData.avatar ? (
                                <img
                                    src={userData.avatar}
                                    alt={userData.username}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <User size={28} color="white" />
                            )}
                        </div>

                        {/* Username */}
                        <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: isDarkMode ? '#e2e8f0' : '#1e293b',
                            marginBottom: '12px'
                        }}>
                            {userData.username}
                        </span>

                        {/* Daily Messages Counter */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.08)',
                            borderRadius: '20px',
                            marginBottom: '12px'
                        }}>
                            <span style={{ fontSize: '16px' }}>🎁</span>
                            <span style={{
                                fontWeight: '600',
                                fontSize: '14px',
                                color: isDarkMode ? '#e2e8f0' : '#334155',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span>{Math.max(0, (userData.usage?.messageLimit || 50) - (userData.usage?.messages || 0))} daily</span>

                                {(userData.usage?.boosterMessages || 0) > 0 && (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        color: '#eab308',
                                        backgroundColor: isDarkMode ? 'rgba(234, 179, 8, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        fontSize: '12px'
                                    }}>
                                        <Zap size={12} fill="currentColor" />
                                        <span>+{userData.usage?.boosterMessages}</span>
                                    </span>
                                )}
                            </span>
                        </div>
                        {/* DEBUG INFO - REMOVE LATER */}
                        <div style={{ fontSize: '10px', color: 'red', textAlign: 'center', marginBottom: '5px' }}>
                            Debug: Avail:{userData.usage?.messagesAvailable} Used:{userData.usage?.messages} Lim:{userData.usage?.messageLimit}
                        </div>

                        {/* Upgrade Button */}
                        {onOpenPricing && !userData.isPro && (
                            <button
                                onClick={() => {
                                    onOpenPricing();
                                    if (onMobileClose) onMobileClose();
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                ✨ Upgrade Plan
                            </button>
                        )}

                        {/* Auth Buttons */}
                        {userData.username === 'Guest' ? (
                            /* Login Button for guests */
                            <button
                                onClick={() => {
                                    if (onLogin) onLogin();
                                    if (onMobileClose) onMobileClose();
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Login / Sign Up
                            </button>
                        ) : (
                            /* Logout for logged users */
                            <button
                                onClick={() => {
                                    if (onLogout) onLogout();
                                    if (onMobileClose) onMobileClose();
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'transparent',
                                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#e2e8f0'}`,
                                    borderRadius: '12px',
                                    color: isDarkMode ? '#f87171' : '#dc2626',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Log Out
                            </button>
                        )}
                    </div>
                )}

                {/* Menu Items */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflowY: 'auto' }}>
                    {/* Plan section - only in chat mode */}
                    {mode === 'chat' && !isCollapsed && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                            borderRadius: '16px',
                            marginBottom: '16px',
                            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px'
                            }}>
                                <span style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>
                                    Plan
                                </span>
                                <span style={{ fontSize: '12px', color: 'rgb(168, 85, 247)', fontWeight: '700' }}>
                                    {userData.isPro ? 'PRO' : 'FREE'}
                                </span>
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                                {/* Daily Messages Row */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#64748b' }}>Messages</span>
                                    <span style={{ fontSize: '12px', color: isDarkMode ? 'white' : '#1e293b', fontFamily: 'monospace' }}>
                                        {userData.usage?.messages || 0}/{userData.usage?.messageLimit || 50}
                                    </span>
                                </div>
                                <div style={{ width: '100%', height: '4px', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0', borderRadius: '2px', overflow: 'hidden', marginBottom: '8px' }}>
                                    <div style={{
                                        width: `${Math.min(100, ((userData.usage?.messages || 0) / (userData.usage?.messageLimit || 50)) * 100)}%`,
                                        height: '100%',
                                        backgroundColor: 'rgb(168, 85, 247)'
                                    }} />
                                </div>

                                {/* Boosters Row - Only if available */}
                                {(userData.usage?.boosterMessages || 0) > 0 && (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', alignItems: 'center' }}>
                                            <span style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Zap size={12} color="#eab308" fill="#eab308" />
                                                Boosters
                                            </span>
                                            <span style={{ fontSize: '12px', color: '#eab308', fontFamily: 'monospace', fontWeight: '700' }}>
                                                {userData.usage?.boosterMessages}
                                            </span>
                                        </div>
                                        {/* Optional: Booster progress bar or just text. Let's keep it simple with text as boosters are "extra" stackable */}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    <MenuItem
                        icon={<Home size={22} />}
                        label="Home"
                        onClick={() => {
                            onHome?.();
                            if (isMobile && onMobileClose) onMobileClose();
                        }}
                        active={true}
                        isCollapsed={isCollapsed}
                        isMobile={isMobile}
                    />

                    {mode !== 'chat' && (
                        <>
                            <MenuItem
                                icon={<Wand2 size={24} />}
                                label="Create Character"
                                onClick={() => {
                                    if (userData.username === 'Guest') {
                                        onLogin?.();
                                    } else {
                                        onCreateCharacter?.();
                                    }
                                    if (isMobile && onMobileClose) onMobileClose();
                                }}
                                isCollapsed={isCollapsed}
                                isMobile={isMobile}
                                requiresLogin={userData.username === 'Guest'}
                            />
                            <MenuItem
                                icon={<Users size={24} />}
                                label="My Characters"
                                onClick={() => {
                                    if (userData.username === 'Guest') {
                                        onLogin?.();
                                    } else {
                                        onMyCharacters?.();
                                    }
                                    if (isMobile && onMobileClose) onMobileClose();
                                }}
                                isCollapsed={isCollapsed}
                                isMobile={isMobile}
                                requiresLogin={userData.username === 'Guest'}
                            />
                            <MenuItem
                                icon={<Sparkles size={24} />}
                                label="Generate Image"
                                onClick={() => {
                                    if (userData.username === 'Guest') {
                                        onLogin?.();
                                    } else {
                                        window.location.href = '/generate';
                                    }
                                    if (isMobile && onMobileClose) onMobileClose();
                                }}
                                isCollapsed={isCollapsed}
                                isMobile={isMobile}
                                requiresLogin={userData.username === 'Guest'}
                            />

                            <MenuItem
                                icon={<MessageSquare size={24} />}
                                label="Suggestions"
                                onClick={() => {
                                    if (userData.username === 'Guest') {
                                        onLogin?.();
                                    } else {
                                        setIsSuggestionsModalOpen(true);
                                    }
                                    if (isMobile && onMobileClose) onMobileClose();
                                }}
                                isCollapsed={isCollapsed}
                                isMobile={isMobile}
                                requiresLogin={userData.username === 'Guest'}
                            />

                            <div style={{ height: isMobile ? '16px' : (isCollapsed ? '10px' : '20px') }}></div>

                            <MenuItem
                                icon={<User size={24} />}
                                label="Profile Settings"
                                onClick={() => {
                                    if (userData.username === 'Guest') {
                                        onLogin?.();
                                    } else {
                                        setIsProfileModalOpen(true);
                                    }
                                    if (isMobile && onMobileClose) onMobileClose();
                                }}
                                isCollapsed={isCollapsed}
                                isMobile={isMobile}
                                requiresLogin={userData.username === 'Guest'}
                            />
                            <MenuItem
                                icon={<Tag size={24} />}
                                label="Pricing & Plans"
                                isCollapsed={isCollapsed}
                                isMobile={isMobile}
                                onClick={() => {
                                    onOpenPricing?.();
                                    if (isMobile && onMobileClose) onMobileClose();
                                }}
                            />
                        </>
                    )}
                    <MenuItem
                        icon={<Bell size={24} />}
                        label="Notifications"
                        isCollapsed={isCollapsed}
                        isMobile={isMobile}
                        requiresLogin={userData.username === 'Guest'}
                        onClick={() => {
                            if (userData.username === 'Guest') {
                                onLogin?.();
                            }
                            if (isMobile && onMobileClose) onMobileClose();
                        }}
                    />
                </div>



                {/* Footer */}
                <div style={{ marginTop: 'auto', borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`, paddingTop: '16px' }}>
                    <MenuItem icon={<MoreHorizontal size={22} />} label="More Options" isCollapsed={isCollapsed} isMobile={isMobile} />
                </div>
            </div >

            {/* Backdrop */}
            {
                !isCollapsed && (
                    <div
                        onClick={() => {
                            setIsCollapsed(true);
                            if (onCollapse) onCollapse(true);
                        }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 999,
                            backdropFilter: 'blur(3px)',
                            display: window.innerWidth < 768 ? 'block' : 'none'
                        }}
                    />
                )
            }

            {/* Profile Modal */}
            <ProfileModal
                isOpen={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
                userData={userData}
                onUpdateProfile={handleUpdateProfile}
            />

            {/* Suggestions Modal */}
            <SuggestionsModal
                isOpen={isSuggestionsModalOpen}
                onClose={() => setIsSuggestionsModalOpen(false)}
            />
        </>
    );
};

const MenuItem = ({ icon, label, onClick, active, isCollapsed, isMobile, requiresLogin }: { icon: React.ReactNode, label: string, onClick?: () => void, active?: boolean, isCollapsed?: boolean, isMobile?: boolean, requiresLogin?: boolean }) => {
    const { isDarkMode } = useTheme();
    const [isHovered, setIsHovered] = React.useState(false);

    const isActuallyActive = active || false;
    // On mobile, always show label
    const shouldShowLabel = isMobile || !isCollapsed;

    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                justifyContent: shouldShowLabel ? 'flex-start' : 'center',
                color: isActuallyActive
                    ? 'white'
                    : (isHovered
                        ? (isDarkMode ? 'white' : 'rgb(126, 34, 206)')
                        : (isDarkMode ? 'rgb(148, 163, 184)' : '#64748b')),
                backgroundColor: isActuallyActive
                    ? 'rgb(168, 85, 247)'
                    : (isHovered
                        ? (isDarkMode ? 'rgba(255,255,255,0.1)' : '#f3e8ff')
                        : 'transparent'),
                borderRadius: '12px',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s',
                boxShadow: isActuallyActive ? 'rgba(168, 85, 247, 0.3) 0px 4px 12px' : 'none',
                whiteSpace: 'nowrap',
                opacity: requiresLogin ? 0.7 : 1
            }}
            title={!shouldShowLabel ? label : (requiresLogin ? `${label} (Login required)` : undefined)}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '24px' }}>
                {icon}
            </div>
            {shouldShowLabel && (
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {label}
                    {requiresLogin && <span style={{ fontSize: '12px', opacity: 0.7 }}>🔒</span>}
                </span>
            )}
        </div>
    );
};

export default Sidebar;


